import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { PlusIcon, PhoneIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { Switch } from '@/components/ui/Switch'
import { EmptyState } from '@/components/common/EmptyState'
import { FormMdxEditor } from '@/components/forms/FormMdxEditor'
import { TransferFlowCard, type TransferRule } from './TransferFlowCard'
import { TransferFlowModal } from './TransferFlowModal'

export function TransferFlowSection() {
  const { t } = useTranslation()
  const { setValue, getValues, watch, control } = useFormContext()
  const [transferRules, setTransferRules] = useState<TransferRule[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [tempRule, setTempRule] = useState<TransferRule | null>(null)
  
  const config = watch('config')

  // Get use_transfer_rules state from config
  const getUseTransferRules = (): boolean => {
    const transferTool = config?.tools?.find((tool: { name: string }) => 
      tool.name === 'call.transfer.v2' || tool.name === 'call.transfer'
    )
    return transferTool?.config?.use_transfer_rules ?? true
  }

  const useTransferRules = getUseTransferRules()

  // Load existing rules when component mounts or config changes
  useEffect(() => {
    const formData = getValues()
    const transferTool = formData?.config?.tools?.find((tool: { name: string }) => 
      tool.name === 'call.transfer.v2' || tool.name === 'call.transfer'
    )
    const rules = transferTool?.config?.transfer_rules || []
    setTransferRules(rules)
  }, [getValues, config])

  const handleToggleUseTransferRules = (checked: boolean) => {
    const formData = getValues()
    const newConfig = { ...formData.config }
    
    if (!newConfig.tools) {
      newConfig.tools = []
    }
    
    const toolIndex = newConfig.tools.findIndex((tool: { name: string }) => 
      tool.name === 'call.transfer.v2' || tool.name === 'call.transfer'
    )

    if (toolIndex === -1) {
      newConfig.tools.push({
        name: 'call.transfer.v2',
        config: {
          use_transfer_rules: checked,
          transfer_rules: []
        }
      })
    } else {
      if (checked && newConfig.tools[toolIndex].name === 'call.transfer') {
        newConfig.tools[toolIndex].name = 'call.transfer.v2'
      }
      newConfig.tools[toolIndex].config = {
        ...newConfig.tools[toolIndex].config,
        use_transfer_rules: checked
      }
    }

    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
  }

  const handleAddRule = () => {
    setTempRule(null)
    setEditingIndex(-1)
  }

  const handleEditRule = (index: number) => {
    setTempRule(transferRules[index])
    setEditingIndex(index)
  }

  const handleDeleteRule = (index: number) => {
    const formData = getValues()
    const newConfig = { ...formData.config }
    const toolIndex = newConfig.tools?.findIndex((tool: { name: string }) => 
      tool.name === 'call.transfer.v2' || tool.name === 'call.transfer'
    )
    
    if (toolIndex !== -1 && toolIndex !== undefined) {
      newConfig.tools[toolIndex].config.transfer_rules = [
        ...transferRules.slice(0, index),
        ...transferRules.slice(index + 1)
      ]
      
      setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
      setTransferRules(newConfig.tools[toolIndex].config.transfer_rules)
    }
  }

  const handleSaveRule = (rule: TransferRule, index: number) => {
    const formData = getValues()
    const newConfig = { ...formData.config }
    
    if (!newConfig.tools) {
      newConfig.tools = []
    }

    let toolIndex = newConfig.tools.findIndex((tool: { name: string }) => 
      tool.name === 'call.transfer.v2' || tool.name === 'call.transfer'
    )
    
    if (toolIndex === -1) {
      newConfig.tools.push({
        name: 'call.transfer.v2',
        config: {
          use_transfer_rules: true,
          transfer_rules: []
        }
      })
      toolIndex = newConfig.tools.length - 1
    }

    if (!newConfig.tools[toolIndex].config) {
      newConfig.tools[toolIndex].config = {}
    }
    if (!newConfig.tools[toolIndex].config.transfer_rules) {
      newConfig.tools[toolIndex].config.transfer_rules = []
    }

    if (index >= 0) {
      newConfig.tools[toolIndex].config.transfer_rules[index] = rule
    } else {
      newConfig.tools[toolIndex].config.transfer_rules.push(rule)
    }
    
    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
    setTransferRules(newConfig.tools[toolIndex].config.transfer_rules)
    setEditingIndex(null)
    setTempRule(null)
  }

  const handleCloseModal = () => {
    setEditingIndex(null)
    setTempRule(null)
  }

  return (
    <div className="space-y-4">
      {/* Use transfer flows toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('resources.assistants.transfer_flows.use_transfer_flows')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.transfer_flows.use_transfer_flows_description')}
          </p>
        </div>
        <Switch
          isSelected={useTransferRules}
          onChange={handleToggleUseTransferRules}
        />
      </div>

      {useTransferRules ? (
        <>
          {/* Transfer flows section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('resources.assistants.transfer_flows.title')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('resources.assistants.transfer_flows.description')}
                </p>
              </div>
              <Button color="secondary" size="sm" onPress={handleAddRule} iconLeading={PlusIcon}>
                {t('resources.assistants.transfer_flows.add')}
              </Button>
            </div>

            {transferRules.length === 0 ? (
              <EmptyState
                variant="card"
                icon={<PhoneIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />}
                title={t('resources.assistants.transfer_flows.empty_title')}
                description={t('resources.assistants.transfer_flows.empty_description')}
                action={{
                  label: t('resources.assistants.transfer_flows.add'),
                  onClick: handleAddRule
                }}
              />
            ) : (
              <div className="space-y-2">
                {transferRules.map((rule, index) => (
                  <TransferFlowCard
                    key={index}
                    rule={rule}
                    index={index}
                    onEdit={() => handleEditRule(index)}
                    onDelete={() => handleDeleteRule(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Modal */}
          <TransferFlowModal
            isOpen={editingIndex !== null}
            onClose={handleCloseModal}
            rule={tempRule}
            index={editingIndex ?? -1}
            onSave={handleSaveRule}
          />
        </>
      ) : (
        /* Call transfer policy textarea when transfer flows are disabled */
        <FormMdxEditor
          control={control}
          name="prompt_scenarios.transfer_policy"
          label={t('resources.assistants.transfer_flows.policy_label')}
          description={t('resources.assistants.transfer_flows.policy_description')}
          placeholder={t('resources.assistants.transfer_flows.policy_placeholder')}
          showDiffView={false}
        />
      )}
    </div>
  )
}

export default TransferFlowSection
